import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { staleGuard, dedupeInFlight } from "src/utils/staleGuard";
import { cannotReadBookings, isBookingsAddOnInactive } from "src/utils/bookings/bookingRules";
import { localISODate } from "src/utils/localDate";
import { getActiveAddOns } from "src/redux/slices/addOns";

// Cargas de fondo de reservas (próximas + alertas): sin add-on «Reservas
// online» el backend responde 402. Tras recargar, estos thunks se lanzaban
// antes de conocer los add-ons activos y `isBookingsAddOnInactive` daba
// «activo» por defecto → 402. Ahora, si aún no se conocen, esperamos a
// /addOn/ActiveIds (dedupeInFlight: comparte la petición en vuelo, no
// añade ninguna) y decidimos con el dato real.
async function backgroundBookingsBlocked(dispatch, getState) {
  let st = getState?.();
  if (st && !st.addOns?.activeAddOnsLoaded) {
    try { await dispatch(getActiveAddOns()); } catch (_) { /* getActiveAddOns ya marca loaded */ }
    st = getState?.();
  }
  return isBookingsAddOnInactive(st) || cannotReadBookings(st);
}

/**
 * Slice de reservas (admin del restaurante). Mantiene la lista completa en
 * memoria y deja que las vistas (calendario / timeline) hagan los filtros
 * client-side por fecha y mesa — el volumen típico de reservas por restaurante
 * es bajo (decenas por día) así que no hace falta paginar.
 */
const initialState = {
  isLoading: false,
  isLoaded: false,
  error: null,
  bookings: [], // BookingGetDto[] — full admin list (vista de reservas)
  // Lista corta de próximas reservas que el TPV poolea cada 60s. La usamos
  // tanto en el widget "Próximas reservas" como en el prompt automático
  // de "Sentar reserva" al abrir una mesa que tiene reserva en ventana.
  // Mantenerla separada de `bookings` evita pisarse con la vista admin si
  // ambas conviven (p.ej. el dueño abre /reservas en otra pestaña).
  upcoming: [],
  // Centro de alertas de reservas (icono del TopBar). Reservas no-vistas +
  // pendientes + próximas. El badge cuenta las `seenByRestaurant === false`.
  alerts: [],
  // Días cerrados (festivos, eventos privados). Cargados bajo demanda por
  // CalendarView con el rango visible del mes. Los pinta como chip rojo y
  // el wizard público los respeta vía endpoint de availability.
  // Cada item: { id, date (ISO yyyy-MM-dd), reason, createdAt }
  closedDays: [],
  // Servicios (turnos) cerrados en una fecha concreta, sin cerrar el día.
  closedShifts: [],
};

const slice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setBookings(state, action) {
      state.isLoading = false;
      state.isLoaded = true;
      state.bookings = action.payload || [];
    },
    setUpcoming(state, action) {
      state.upcoming = action.payload || [];
    },
    setAlerts(state, action) {
      state.alerts = action.payload || [];
    },
    upsertBooking(state, action) {
      const b = action.payload;
      if (!b) return;
      const idx = state.bookings.findIndex((x) => x.id === b.id);
      if (idx >= 0) state.bookings[idx] = { ...state.bookings[idx], ...b };
      else state.bookings.unshift(b);

      // Mantener sincronizado el subset `upcoming` sin refetch:
      // 1) Si YA está en la lista → merge del cambio (Seated/Completed/etc.).
      // 2) Si NO está y la reserva es "próxima" (fecha futura y status vivo:
      //    PendingApproval/Confirmed/Seated), la insertamos. Así una reserva
      //    creada por el hub aparece al instante en TpvUpcomingReservations
      //    sin que necesitemos un refetch /booking/Upcoming.
      // 3) Si estaba y pasó a estado terminal (Cancelled/NoShow/Completed),
      //    la sacamos del subset — ya no es "próxima".
      const uIdx = state.upcoming.findIndex((x) => x.id === b.id);
      const isTerminal = b.status === 3 || b.status === 4 || b.status === 5;
      const isFuture = (() => {
        try {
          const raw = String(b.date || "");
          if (!raw) return false;
          return raw.slice(0, 10) >= localISODate();
        } catch (_) {
          return false;
        }
      })();

      if (uIdx >= 0) {
        if (isTerminal) state.upcoming.splice(uIdx, 1);
        else state.upcoming[uIdx] = { ...state.upcoming[uIdx], ...b };
      } else if (!isTerminal && isFuture) {
        state.upcoming.push(b);
        state.upcoming.sort((a, z) =>
          String(a.date).localeCompare(String(z.date)),
        );
      }
    },
    removeBooking(state, action) {
      const id = action.payload;
      state.bookings = state.bookings.filter((b) => b.id !== id);
      state.upcoming = state.upcoming.filter((b) => b.id !== id);
    },
    setClosedDays(state, action) {
      state.closedDays = Array.isArray(action.payload) ? action.payload : [];
    },
    upsertClosedDay(state, action) {
      const d = action.payload;
      if (!d) return;
      // Comparación por fecha (string YYYY-MM-DD del backend o ISO completo).
      const key = (x) => String(x.date).slice(0, 10);
      const idx = state.closedDays.findIndex((x) => key(x) === key(d));
      if (idx >= 0) state.closedDays[idx] = { ...state.closedDays[idx], ...d };
      else state.closedDays.push(d);
    },
    setClosedShifts(state, action) {
      state.closedShifts = Array.isArray(action.payload) ? action.payload : [];
    },
    upsertClosedShifts(state, action) {
      const rows = Array.isArray(action.payload) ? action.payload : [];
      const key = (x) => `${String(x.date).slice(0, 10)}#${x.bookingShiftId}`;
      for (const r of rows) {
        const idx = state.closedShifts.findIndex((x) => key(x) === key(r));
        if (idx >= 0) state.closedShifts[idx] = { ...state.closedShifts[idx], ...r };
        else state.closedShifts.push(r);
      }
    },
    removeClosedShift(state, action) {
      const { date, shiftId } = action.payload || {};
      const d = String(date).slice(0, 10);
      state.closedShifts = state.closedShifts.filter(
        (x) => !(String(x.date).slice(0, 10) === d && x.bookingShiftId === shiftId),
      );
    },
    removeClosedDayByDate(state, action) {
      const date = String(action.payload).slice(0, 10);
      state.closedDays = state.closedDays.filter(
        (x) => String(x.date).slice(0, 10) !== date,
      );
    },
  },
});

export default slice.reducer;
export const {
  setBookings: setBookingsLocal,
  // Acciones expuestas para el hub SignalR de reservas (F2 push real-time):
  // el push del backend trae el DTO completo, así que el hub aplica el
  // upsert directamente sin pasar por una thunk HTTP.
  upsertBooking: upsertBookingLocal,
  removeBooking: removeBookingLocal,
  // Expuestas para hidratación desde el bootstrap del TPV (evita re-fetch).
  setUpcoming,
  setAlerts,
} = slice.actions;

// Trae las próximas N reservas del TPV. La lista se hidrata inicialmente
// desde `bootstrap/tpv` y a partir de ahí BookingHub (SignalR) empuja
// los cambios en vivo por `upsertBookingLocal`/`removeBookingLocal` sin
// necesidad de refetch. Este thunk solo se usa cuando REALMENTE hace
// falta refrescar (bootstrap falló, hub se reconectó, o vista /reservas
// explícita) — nunca en polling ciego.
//
// Default 30 (antes 8) — restaurantes con 40+ mesas pueden tener 20-30
// reservas próximas en ventana de 2h; con 8 se cortaban silenciosamente.
export function fetchUpcomingBookings({ limit = 30, force = false } = {}) {
  return async (dispatch, getState) => {
    // Sin add-on "Reservas online" el TPV no muestra reservas ni llama
    // al backend (respondería 402).
    if (await backgroundBookingsBlocked(dispatch, getState)) {
      dispatch(slice.actions.setUpcoming([]));
      return;
    }
    // staleGuard TTL 5min sigue actuando como cortafuegos ante
    // llamadas duplicadas (montaje/desmontaje rápido). `force: true`
    // salta la guarda — se usa desde el hub al reconectar y desde la
    // vista /reservas cuando el user pulsa recargar.
    if (!force && staleGuard(`bookings/upcoming/${limit}`)) return;
    // dedupeInFlight blindaje adicional: si dos componentes disparan
    // este thunk en el mismo tick, la segunda comparte la promise
    // de la primera en lugar de hacer un GET separado. staleGuard
    // marca timestamp pero no bloquea peticiones simultáneas.
    return dedupeInFlight(`bookings/upcoming/${limit}`, async () => {
      try {
        const res = await axios.get(`/booking/Upcoming?limit=${limit}`);
        dispatch(slice.actions.setUpcoming(Array.isArray(res.data) ? res.data : []));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("fetchUpcomingBookings error:", e?.message || e);
      }
    });
  };
}

// `silent: true` evita que se dispare startLoading() — útil para refrescos
// de fondo (polling automático cada 60s) que no deben pintar el spinner ni
// hacer parpadear el contenido. La carga inicial y el botón "Recargar"
// manual siguen usando el modo no-silencioso para feedback visual.
export function fetchBookings({ silent = false } = {}) {
  return async (dispatch) => {
    if (!silent) dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get("/booking/Get");
      dispatch(slice.actions.setBookings(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error cargando reservas"));
    }
  };
}

// Centro de alertas de reservas (icono TopBar). Trae no-vistas + pendientes
// + próximas. Silencioso (no spinner). Se llama al montar el bell, por
// polling y tras cada push del BookingHub.
export function fetchBookingAlerts({ force = false } = {}) {
  return async (dispatch, getState) => {
    if (await backgroundBookingsBlocked(dispatch, getState)) {
      dispatch(slice.actions.setAlerts([]));
      return;
    }
    if (!force && staleGuard("bookings/alerts")) return;
    return dedupeInFlight("bookings/alerts", async () => {
      try {
        const res = await axios.get("/booking/Alerts");
        dispatch(slice.actions.setAlerts(Array.isArray(res.data) ? res.data : []));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("fetchBookingAlerts error:", e?.message || e);
      }
    });
  };
}

// Marca reservas como vistas (POST /booking/MarkSeen). ids vacío = todas las
// no-vistas. Tras marcar, refresca el centro de alertas para vaciar el badge.
//
// El refetch va con `force: true` a propósito: el fetch inicial de la
// campana marcó `staleGuard("bookings/alerts")` hace segundos, así que un
// dispatch sin `force` se saltaría el GET y el state local seguiría con
// `seenByRestaurant=false` en las alertas — badge congelado y notifs
// que siguen apareciendo tras marcarlas vistas.
export function markBookingsSeen(ids = []) {
  return async (dispatch) => {
    try {
      await axios.post("/booking/MarkSeen", ids);
      dispatch(fetchBookingAlerts({ force: true }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("markBookingsSeen error:", e?.message || e);
    }
  };
}

// Consulta slots disponibles para una fecha + pax. Se invoca desde el
// modal "Nueva reserva" en el paso 2. No persistimos los slots en el slice
// porque dependen de los inputs del usuario y caducan cada vez que cambia
// algún parámetro — devolver la promesa al componente es suficiente.
export function fetchBookingAvailability({ date, pax, zoneId }) {
  return async () => {
    const params = {};
    if (date) params.date = date;
    if (pax) params.pax = pax;
    if (zoneId != null && zoneId !== "") params.zoneId = zoneId;
    const res = await axios.get("/booking/Availability", { params });
    return Array.isArray(res.data) ? res.data : [];
  };
}

// Crea una reserva manual desde el admin (botón "+ Nueva reserva" de la
// vista plano). El backend (POST /booking/Create) genera ReservationCODE,
// valida choque de mesa y devuelve BookingGetDto. Al éxito hacemos upsert
// en el slice para que la vista la pinte sin necesidad de refetch.
export function createBooking(dto) {
  return async (dispatch) => {
    try {
      const res = await axios.post("/booking/Create", dto);
      dispatch(slice.actions.upsertBooking(res.data));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error creando reserva"));
      throw e;
    }
  };
}

export function confirmBooking(id) {
  return async (dispatch) => {
    try {
      const res = await axios.put(`/booking/ConfirmBooking/${id}`);
      dispatch(slice.actions.upsertBooking(res.data));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error confirmando"));
      throw e;
    }
  };
}

export function cancelBookingAdmin(id) {
  return async (dispatch) => {
    try {
      await axios.delete(`/booking/Delete/${id}`);
      dispatch(slice.actions.removeBooking(id));
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error cancelando"));
      throw e;
    }
  };
}

// Marca la reserva como SENTADA. Si se pasa `sessionId` (flujo "sentar al
// abrir mesa", que acaba de crear la sesión), el backend vincula la reserva
// a ESA sesión exacta — sin lookup por mesa, evitando que coja una sesión
// anterior colgada. Sin sessionId, el backend usa mesa.SessionId. El front
// actualiza el estado para que la card de la mesa cambie color y el sidebar
// refleje "Sentados — Cliente X".
export function markBookingSeated(id, sessionId = null) {
  return async (dispatch) => {
    try {
      const url = sessionId
        ? `/booking/MarkSeated/${id}?sessionId=${sessionId}`
        : `/booking/MarkSeated/${id}`;
      const res = await axios.post(url);
      dispatch(slice.actions.upsertBooking(res.data));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error sentando"));
      throw e;
    }
  };
}

// Marca manualmente como Finalizada (Completed). Útil cuando una reserva
// se quedó en Seated tras irse el cliente sin que el TPV registrara el
// cobro (cobros offline, cierre forzado, etc.). El path normal es el
// auto-Complete via `BookingService.CompleteForSession` al cerrar sesión.
export function markBookingCompleted(id) {
  return async (dispatch) => {
    try {
      const res = await axios.post(`/booking/MarkCompleted/${id}`);
      dispatch(slice.actions.upsertBooking(res.data));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error finalizando reserva"));
      throw e;
    }
  };
}

// Marca AUSENTE — anula la reserva (IsActive=false) y libera la mesa. Si
// había sesión asociada se desvincula (no se cierra: el camarero decide
// qué hacer con la sesión).
export function markBookingNoShow(id) {
  return async (dispatch) => {
    try {
      const res = await axios.post(`/booking/MarkNoShow/${id}`);
      // El backend devuelve IsActive=false; replicamos quitándola de la lista.
      dispatch(slice.actions.removeBooking(id));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error marcando ausente"));
      throw e;
    }
  };
}

// Restaura una reserva cancelada/ausente cuya fecha aún no ha pasado.
// Vuelve a marcar IsActive=true y Status=Confirmed. El backend rechaza si la
// fecha ya pasó (más de 30 min de margen).
export function restoreBooking(id) {
  return async (dispatch) => {
    try {
      const res = await axios.post(`/booking/Restore/${id}`);
      dispatch(slice.actions.upsertBooking(res.data));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error restaurando"));
      throw e;
    }
  };
}

export function cancelBookingExplicit(id) {
  return async (dispatch) => {
    try {
      const res = await axios.post(`/booking/Cancel/${id}`);
      dispatch(slice.actions.removeBooking(id));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error cancelando"));
      throw e;
    }
  };
}

// F5 — Histórico del cliente vinculado a una reserva. No persistimos en el
// slice porque cada drawer carga sus datos al abrir y son específicos del
// cliente; retornamos la promesa para que el componente gestione su estado
// local (loading + error + data).
export function fetchCustomerHistory(idCustomer) {
  return async () => {
    if (!idCustomer) return null;
    const res = await axios.get(`/booking/Customer/${idCustomer}/History`);
    return res?.data || null;
  };
}

// RES-5 — Lista de clientes del restaurante con KPIs (tab "Clientes"). search
// filtra por nombre/email/teléfono. Devuelve la promesa; el componente
// gestiona su estado local (no persiste en el slice).
export function fetchBookingCustomers(search = "") {
  return async () => {
    const res = await axios.get("/booking/Customers", {
      params: search ? { search } : {},
    });
    return Array.isArray(res.data) ? res.data : [];
  };
}

/**
 * Update parcial — útil para reasignar mesa, cambiar fecha o reducir comensales
 * desde el drawer de detalle.
 */
export function updateBooking(dto) {
  return async (dispatch) => {
    try {
      const res = await axios.put("/booking/Update", dto);
      dispatch(slice.actions.upsertBooking(res.data));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error actualizando"));
      throw e;
    }
  };
}

// ── Días cerrados (festivos / vacaciones / eventos privados) ─────────────

// Trae los días cerrados en un rango [from, to]. Llamado por CalendarView
// al cambiar de mes para repintar los chips. Silencioso.
export function fetchClosedDays(from, to) {
  return async (dispatch) => {
    try {
      const fromIso = toIsoDate(from);
      const toIso = toIsoDate(to);
      const res = await axios.get(`/booking/closedDays?from=${fromIso}&to=${toIso}`);
      dispatch(slice.actions.setClosedDays(Array.isArray(res.data) ? res.data : []));
      return res.data;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("fetchClosedDays error:", e?.message || e);
      return [];
    }
  };
}

// Marca un día como cerrado. El backend rechaza con 400 si hay reservas
// activas — el caller debe mostrar el mensaje al admin y proponer cancelar
// las reservas primero. Devuelve el dto creado.
export function closeDay(date, reason) {
  return async (dispatch) => {
    const res = await axios.post("/booking/closedDays", {
      date: toIsoDate(date),
      reason: reason || null,
    });
    dispatch(slice.actions.upsertClosedDay(res.data));
    return res.data;
  };
}

// Reabre un día (elimina la entrada). Idempotente.
export function reopenDay(date) {
  return async (dispatch) => {
    const iso = toIsoDate(date);
    await axios.delete(`/booking/closedDays?date=${iso}`);
    dispatch(slice.actions.removeClosedDayByDate(iso));
  };
}

// ── Servicios cerrados en una fecha (comida cerrada, cena abierta) ──────

// Trae los servicios cerrados en [from, to]. Silencioso como fetchClosedDays.
export function fetchClosedShifts(from, to) {
  return async (dispatch) => {
    try {
      const res = await axios.get(
        `/booking/closedShifts?from=${toIsoDate(from)}&to=${toIsoDate(to)}`,
      );
      dispatch(slice.actions.setClosedShifts(Array.isArray(res.data) ? res.data : []));
      return res.data;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("fetchClosedShifts error:", e?.message || e);
      return [];
    }
  };
}

// Nº de reservas activas en esos servicios esa fecha (para avisar antes de
// cerrar; el cierre NO las cancela).
export async function fetchCloseShiftsImpact(date, shiftIds) {
  const params = new URLSearchParams();
  params.append("date", toIsoDate(date));
  for (const id of shiftIds || []) params.append("shiftIds", String(id));
  const res = await axios.get(`/booking/closedShifts/impact?${params.toString()}`);
  return Number(res?.data?.activeBookingsCount) || 0;
}

// Cierra uno o varios servicios esa fecha. Devuelve las filas cerradas.
export function closeShifts(date, shiftIds, reason) {
  return async (dispatch) => {
    const res = await axios.post("/booking/closedShifts", {
      date: toIsoDate(date),
      shiftIds,
      reason: reason || null,
    });
    dispatch(slice.actions.upsertClosedShifts(res.data));
    return res.data;
  };
}

// Reabre un servicio esa fecha. Idempotente.
export function reopenShift(date, shiftId) {
  return async (dispatch) => {
    const iso = toIsoDate(date);
    await axios.delete(`/booking/closedShifts?date=${iso}&shiftId=${shiftId}`);
    dispatch(slice.actions.removeClosedShift({ date: iso, shiftId }));
  };
}

// Helper: cualquier formato (Date, string ISO, "YYYY-MM-DDTHH:mm…") a
// "YYYY-MM-DD" — el backend acepta solo la fecha (DateTime → DATE).
function toIsoDate(value) {
  if (!value) return "";
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return String(value).slice(0, 10);
}
